import { EnvironmentProviders, inject, makeEnvironmentProviders, provideAppInitializer } from '@angular/core';
import {
  ContextFallbackService,
  ContextSelectorService,
  CurrentUser,
  CurrentUserService,
  PROFILE_REQUIRED_FOR_USER,
} from '@rma/generic/ng/user';
import { TokenProviderService } from '@rma/public/generic/util/http';
import { firstValueFrom, tap } from 'rxjs';

function initialiseAuth(
  tokenProvider: TokenProviderService,
  currentUserService: CurrentUserService,
  contextSelectorService: ContextSelectorService,
  contextFallbackService: ContextFallbackService,
) {
  return async () => {
    contextFallbackService.initialise();

    // No user token -> no point hitting /me. Push a null user (consumers/angulartics rely on the emission). We still
    // call the selector's initialise so its storage-writer is wired (a later sign-in's setContext must persist), and
    // it resolves to no context for a null user. This is also the usual server-side path (no token during SSR), where
    // the selector's own isPlatformServer guard makes initialise a no-op.
    if (!tokenProvider.hasUserToken()) {
      currentUserService.onLogout();
      contextSelectorService.initialise(null);
      return;
    }

    await firstValueFrom(currentUserService.reloadUser().pipe(tap((x) => contextSelectorService.initialise(x))));
  };
}

export function provideRmaUser(): EnvironmentProviders {
  return makeEnvironmentProviders([
    // Public serves Consumers, who legitimately have no profile, so a null resolved context is valid for them and must
    // not trigger the reset-to-default fallback. Everyone else (an Agent) is expected to have a resolvable profile.
    { provide: PROFILE_REQUIRED_FOR_USER, useValue: (user: CurrentUser) => user.userType !== 'Consumer' },
    provideAppInitializer(() => {
      const initializerFn = initialiseAuth(
        inject(TokenProviderService),
        inject(CurrentUserService),
        inject(ContextSelectorService),
        inject(ContextFallbackService),
      );
      return initializerFn();
    }),
  ]);
}
